import {
  useState,
  useContext,
  useEffect,
  lazy,
  Suspense,
  type FormEvent,
} from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUpRight,
  ArrowDownLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Plus,
  LayoutDashboard,
  Wallet,
  CreditCard,
  CalendarDays,
  Ellipsis,
  Eye,
  EyeOff,
  ShieldCheck,
  Sparkles,
  LogOut,
  Leaf,
  Check,
  Landmark,
  Info,
  Search,
  FileSpreadsheet,
  type LucideIcon,
} from "lucide-react";
import { Modal } from "./components/Modal.tsx";
import { ManagedForm } from "./components/ManagedForm.tsx";
import { RecoveryCodes } from "./features/security/RecoveryCodes.tsx";
import { ChangePassword } from "./features/security/ChangePassword.tsx";
import QRCode from "qrcode";
import {
  minor,
  parseBRL,
  overview,
  type FinancialAccount,
  type Movement,
} from "@clareza/domain";
import { categories } from "@clareza/contracts";
import { api } from "./api.ts";
import { Privacy } from "./contexts/privacy.ts";
import { Money, DateLabel, Panel, Empty } from "./components/finance-ui.tsx";
type Overview = ReturnType<typeof overview>;
const Planning = lazy(() =>
  import("./features/planning/PlanningPage.tsx").then((module) => ({
    default: module.PlanningPage,
  })),
);
const CardsPage = lazy(() =>
  import("./features/cards/CardsPage.tsx").then((module) => ({
    default: module.CardsPage,
  })),
);
const AccountEditor = lazy(() =>
  import("./features/manual/AccountEditor.tsx").then((module) => ({
    default: module.AccountEditor,
  })),
);
const MovementDetail = lazy(() =>
  import("./features/manual/MovementDetail.tsx").then((module) => ({
    default: module.MovementDetail,
  })),
);
const TransfersPanel = lazy(() =>
  import("./features/manual/TransfersPanel.tsx").then((module) => ({
    default: module.TransfersPanel,
  })),
);
const CsvImportPanel = lazy(() =>
  import("./features/manual/CsvImportPanel.tsx").then((module) => ({
    default: module.CsvImportPanel,
  })),
);
const OpenFinancePanel = lazy(() =>
  import("./features/accounts/OpenFinancePanel.tsx").then((module) => ({
    default: module.OpenFinancePanel,
  })),
);
type Session = {
  authenticated: boolean;
  mfaComplete: boolean;
  mfaEnabled?: boolean;
  bootstrapRequired?: boolean;
  name?: string;
};
const issueLabels: Record<string, string> = {
  reserve_plan_missing:
    "O aporte para a reserva ainda não foi planejado neste mês.",
  transfer_fee_missing: "Uma transferência tem tarifa ainda não informada.",
  income_missing: "Informe sua renda para calcular a sobra.",
  account_balances_missing: "Há saldos de contas ainda não informados.",
  commitment_amount_missing: "Um compromisso está sem valor.",
  commitment_date_missing: "Um compromisso está sem vencimento.",
  bill_composition_missing:
    "A composição de uma fatura ainda não está disponível.",
  bill_divergence: "O total da fatura diverge dos lançamentos.",
  card_cycle_needs_review: "Confirme o ciclo dos lançamentos de cartão.",
  other_currencies_excluded:
    "Valores em outras moedas estão fora deste resumo.",
  balance_dates_inconsistent: "As datas dos saldos precisam ser conferidas.",
  movements_need_review: "Há lançamentos que precisam de revisão.",
  bill_date_missing: "Uma fatura está sem vencimento.",
  bill_total_missing: "Uma fatura está sem total.",
  bill_overpayment: "Há um pagamento acima do total da fatura para conciliar.",
  bill_composition_credit:
    "Os estornos superam as compras de um ciclo. Confirme o crédito com a instituição.",
};
function Brand() {
  return (
    <span className="brand">
      <span className="brand-icon">
        <Leaf size={22} strokeWidth={2.2} />
      </span>
      clareza<span className="brand-dot">.</span>
    </span>
  );
}
export function App() {
  const location = useLocation(),
    navigate = useNavigate(),
    client = useQueryClient();
  const demo = location.pathname.startsWith("/demo");
  const [hidden, setHidden] = useState(false),
    [month, setMonth] = useState("2026-10"),
    [entry, setEntry] = useState(false);
  const session = useQuery({
    queryKey: ["session"],
    queryFn: () => api<{ data: Session }>("/api/session"),
    retry: false,
  });
  const prefix = demo ? "/api/demo" : "/api";
  const summary = useQuery({
    queryKey: ["overview", demo, month],
    queryFn: () => api<{ data: Overview }>(`${prefix}/overview?month=${month}`),
    enabled: demo || session.data?.data.mfaComplete === true,
  });
  if (!demo && !session.data?.data.mfaComplete)
    return (
      <Welcome
        session={session.data?.data}
        loading={session.isPending}
        error={session.error?.message}
        onAuthenticated={async () => {
          await client.invalidateQueries({ queryKey: ["session"] });
          navigate("/inicio");
        }}
      />
    );
  const area = location.pathname.split("/").at(-1) || "inicio",
    base = demo ? "/demo" : "";
  const names: Record<string, string> = {
    inicio: "Visão do mês",
    movimentos: "Movimentos",
    cartoes: "Cartões",
    planejamento: "Planejamento",
    mais: "Seu espaço",
    contas: "Contas",
    assistente: "Assistente",
  };
  async function logout() {
    if (!demo) await api("/api/auth/sign-out", {});
    client.clear();
    setHidden(false);
    navigate("/");
  }
  const nav: { path: string; label: string; icon: LucideIcon }[] = [
    { path: "inicio", label: "Início", icon: LayoutDashboard },
    { path: "movimentos", label: "Movimentos", icon: Wallet },
    { path: "cartoes", label: "Cartões", icon: CreditCard },
    { path: "planejamento", label: "Planejamento", icon: CalendarDays },
    { path: "mais", label: "Mais", icon: Ellipsis },
  ];
  return (
    <Privacy.Provider value={hidden}>
      <div className="app-shell">
        <aside className="sidebar">
          <Link to={`${base}/inicio`} aria-label="Clareza início">
            <Brand />
          </Link>
          <div className="sidebar-label">MEU DIA A DIA</div>
          <nav aria-label="Navegação principal">
            {nav.map(({ path, label, icon: Icon }) => (
              <NavLink key={path} to={`${base}/${path}`}>
                <Icon size={20} />
                <span>{label}</span>
              </NavLink>
            ))}
            <div className="nav-divider" />
            <NavLink to={`${base}/contas`}>
              <Landmark size={20} />
              <span>Contas</span>
            </NavLink>
            <NavLink to={`${base}/assistente`}>
              <Sparkles size={20} />
              <span>Assistente</span>
              <span className="tiny-tag">IA</span>
            </NavLink>
          </nav>
          <div className="sidebar-bottom">
            <div className="private-note">
              <ShieldCheck size={18} />
              <span>
                Um espaço só seu.
                <br />
                <small>Seus números, no seu controle.</small>
              </span>
            </div>
            <button className="profile-button" onClick={logout}>
              <span className="avatar">
                {demo ? "E" : (session.data?.data.name?.slice(0, 1) ?? "M")}
              </span>
              <span>
                {demo
                  ? "Conta de exemplo"
                  : (session.data?.data.name ?? "Minha conta")}
                <small>
                  {demo ? "Sair da demonstração" : "Encerrar sessão"}
                </small>
              </span>
              <LogOut size={17} />
            </button>
          </div>
        </aside>
        <div className="workspace">
          <header className="topbar">
            <span className="breadcrumb">
              Meu espaço <span>/</span> {names[area] ?? "Início"}
            </span>
            <div className="top-actions">
              <span className="local-status">
                <span />
                Ambiente local
              </span>
              <button
                className="icon-button"
                onClick={() => setHidden(!hidden)}
                aria-label={hidden ? "Mostrar valores" : "Ocultar valores"}
              >
                {hidden ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </header>
          {demo && (
            <div className="demo-banner">
              <span>
                <Info size={16} />
                <strong>Dados de exemplo</strong>
                <span className="demo-description">
                  {" "}
                  · Explore com tranquilidade. Estes números são fictícios.
                </span>
              </span>
              <button onClick={logout}>
                Usar meus dados <ArrowRight size={15} />
              </button>
            </div>
          )}
          <main id="main-content">
            <div className="page-heading">
              <div>
                <div className="eyebrow">
                  {area === "inicio"
                    ? "UM PASSO DE CADA VEZ"
                    : "SUAS FINANÇAS, ORGANIZADAS"}
                </div>
                <h1>
                  {names[area] ?? "Visão do mês"}
                  {area === "inicio" && <span className="heading-dot">.</span>}
                </h1>
                <p>
                  {area === "inicio"
                    ? "Entenda o que aconteceu e o que vem pela frente."
                    : area === "movimentos"
                      ? "Cada lançamento conta uma parte da sua história."
                      : area === "cartoes"
                        ? "Compras, faturas e pagamentos. Cada um no seu lugar."
                        : area === "planejamento"
                          ? "Dê um destino ao seu dinheiro, sem perder de vista o mês."
                          : "Tudo o que você precisa para cuidar dos seus dados."}
                </p>
              </div>
              <div className="page-controls">
                <label className="month-control">
                  <CalendarDays size={17} />
                  <span className="sr-only">Mês selecionado</span>
                  <input
                    aria-label="Mês selecionado"
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  />
                </label>
                {!demo && (
                  <button
                    className="button primary"
                    onClick={() => setEntry(true)}
                  >
                    <Plus size={18} />
                    Registrar
                  </button>
                )}
              </div>
            </div>
            {summary.isPending ? (
              <div className="loading" role="status">
                Preparando seu resumo…
              </div>
            ) : summary.isError ? (
              <Panel>
                <Empty
                  title="Seu resumo não carregou"
                  description={summary.error.message}
                  action={
                    <button
                      className="button primary"
                      onClick={() => summary.refetch()}
                    >
                      Tentar novamente
                    </button>
                  }
                />
              </Panel>
            ) : (
              summary.data && (
                <>
                  {area === "inicio" || area === "" ? (
                    <Dashboard
                      data={summary.data.data}
                      prefix={prefix}
                      month={month}
                      base={base}
                    />
                  ) : area === "movimentos" ? (
                    <Movements prefix={prefix} month={month} demo={demo} />
                  ) : area === "cartoes" ? (
                    <Suspense
                      fallback={<p role="status">Preparando seus cartões…</p>}
                    >
                      <CardsPage
                        data={summary.data.data}
                        month={month}
                        demo={demo}
                        prefix={prefix}
                      />
                    </Suspense>
                  ) : area === "planejamento" ? (
                    <Suspense
                      fallback={<p role="status">Preparando planejamento…</p>}
                    >
                      <Planning
                        prefix={prefix}
                        month={month}
                        demo={demo}
                        summary={summary.data.data}
                      />
                    </Suspense>
                  ) : area === "contas" ? (
                    <Accounts prefix={prefix} demo={demo} month={month} />
                  ) : area === "assistente" ? (
                    <Assistant prefix={prefix} />
                  ) : (
                    <More base={base} demo={demo} />
                  )}
                </>
              )
            )}
          </main>
          <footer className="footer">
            <span>
              <ShieldCheck size={14} /> Sem operações bancárias de pagamento
            </span>
            <span>Clareza · versão local 0.1</span>
          </footer>
        </div>
        <nav className="mobile-nav" aria-label="Navegação no celular">
          {nav.map(({ path, label, icon: Icon }) => (
            <NavLink key={path} to={`${base}/${path}`}>
              <Icon size={19} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <EntryForm open={entry} onOpenChange={setEntry} month={month} />
      </div>
    </Privacy.Provider>
  );
}
function Welcome({
  session,
  loading,
  error,
  onAuthenticated,
}: {
  session?: Session;
  loading: boolean;
  error?: string;
  onAuthenticated: () => Promise<void>;
}) {
  const [login, setLogin] = useState(false),
    [step, setStep] = useState<"credentials" | "verify" | "enroll">(
      "credentials",
    ),
    [message, setMessage] = useState(""),
    [useRecoveryCode, setUseRecoveryCode] = useState(false),
    [busy, setBusy] = useState(false),
    [enrollment, setEnrollment] = useState<{
      totpURI: string;
      backupCodes: string[];
    } | null>(null),
    [qr, setQr] = useState("");
  useEffect(() => {
    if (session?.authenticated && !session.mfaComplete)
      setStep(session.mfaEnabled ? "verify" : "enroll");
  }, [session]);
  useEffect(() => {
    if (enrollment) {
      let active = true;
      QRCode.toDataURL(enrollment.totpURI, { width: 200, margin: 1 }).then(
        (url) => {
          if (active) setQr(url);
        },
      );
      return () => {
        active = false;
      };
    }
  }, [enrollment]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const fields = new FormData(event.currentTarget);
    try {
      if (step === "credentials") {
        const result = await api<{ twoFactorRedirect?: boolean }>(
          "/api/auth/sign-in/email",
          { email: fields.get("email"), password: fields.get("password") },
        );
        if (result.twoFactorRedirect) setStep("verify");
        else {
          setStep("enroll");
          await onAuthenticated();
        }
      } else if (step === "enroll" && !enrollment) {
        const result = await api<{ totpURI: string; backupCodes: string[] }>(
          "/api/auth/two-factor/enable",
          { password: fields.get("password") },
        );
        setEnrollment(result);
      } else {
        await api(
          step === "verify" && useRecoveryCode
            ? "/api/auth/two-factor/verify-backup-code"
            : "/api/auth/two-factor/verify-totp",
          {
            code: String(fields.get("code") ?? "").trim(),
            trustDevice: false,
          },
        );
        setEnrollment(null);
        setQr("");
        setUseRecoveryCode(false);
        await onAuthenticated();
      }
    } catch (e) {
      setMessage(
        e instanceof Error ? e.message : "Confira os dados e tente novamente.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="welcome">
      <header>
        <Brand />
        <span className="local-status">
          <span />
          Seu espaço pessoal
        </span>
      </header>
      <div className="welcome-grid">
        <section>
          <div className="eyebrow">MENOS RUÍDO. MAIS CONTROLE.</div>
          <h1>
            Suas finanças,
            <br />
            com mais <em>clareza.</em>
          </h1>
          <p>
            Reúna o que entrou, o que saiu e o que ainda vem pela frente. Com
            números que fazem sentido e espaço para planejar.
          </p>
          <div className="welcome-actions">
            <button className="button primary" onClick={() => setLogin(true)}>
              Começar com meus dados <ArrowRight size={18} />
            </button>
            <Link className="button secondary" to="/demo/inicio">
              Ver demonstração
            </Link>
          </div>
          <div className="welcome-points">
            <span>
              <Check size={16} />
              Orçamento e saldo separados
            </span>
            <span>
              <Check size={16} />
              Sem contar um gasto duas vezes
            </span>
            <span>
              <Check size={16} />
              Você decide o que compartilhar
            </span>
          </div>
        </section>
        <div className="welcome-preview" aria-label="Exemplo visual fictício">
          <div className="preview-top">
            <span>UM MÊS MAIS TRANQUILO</span>
            <Leaf size={22} />
          </div>
          <span className="preview-label">
            Encontre espaço para seus planos.
          </span>
          <div className="preview-line">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
          <div className="preview-bottom">
            <ShieldCheck size={20} />
            <div>
              Comece no seu ritmo.
              <small>Os dados desconhecidos continuam desconhecidos.</small>
            </div>
          </div>
        </div>
      </div>
      <footer>
        Um aplicativo pessoal. Feito para entender, não para julgar.
      </footer>
      <Modal
        open={login}
        onOpenChange={setLogin}
        title={
          step === "credentials"
            ? "Bem-vindo ao seu espaço"
            : step === "verify"
              ? "Confirme seu acesso"
              : "Proteja sua conta"
        }
        description={
          step === "credentials"
            ? "Use sua conta local para continuar."
            : "Seus dados financeiros só ficam disponíveis após o segundo fator."
        }
      >
        {loading ? (
          <p>Conferindo acesso…</p>
        ) : error ? (
          <p role="alert">O servidor local está indisponível. {error}</p>
        ) : session?.bootstrapRequired ? (
          <div className="notice">
            <ShieldCheck size={24} />
            <p>
              Sua conta pessoal ainda precisa ser preparada no computador. As
              instruções de execução local acompanham o projeto. Você já pode
              explorar a demonstração.
            </p>
            <Link className="button primary" to="/demo/inicio">
              Ver demonstração
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="form">
            {step === "credentials" ? (
              <>
                <label>
                  E-mail
                  <input
                    name="email"
                    type="email"
                    required
                    autoComplete="username"
                  />
                </label>
                <label>
                  Senha
                  <input
                    name="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    minLength={12}
                  />
                </label>
              </>
            ) : step === "enroll" && !enrollment ? (
              <label>
                Confirme sua senha
                <input
                  name="password"
                  type="password"
                  required
                  autoComplete="current-password"
                />
              </label>
            ) : (
              <>
                {enrollment && (
                  <div className="enrollment">
                    <p>Escaneie com seu aplicativo autenticador.</p>
                    {qr && (
                      <img
                        src={qr}
                        width="200"
                        height="200"
                        alt="QR Code para configurar seu autenticador"
                      />
                    )}
                    <details>
                      <summary>Chave para configuração manual</summary>
                      <code>
                        {new URL(enrollment.totpURI).searchParams.get("secret")}
                      </code>
                    </details>
                    <details>
                      <summary>Guarde seus códigos de recuperação</summary>
                      <p>
                        Guarde fora deste computador. Cada código funciona uma
                        vez.
                      </p>
                      <pre>{enrollment.backupCodes.join("\n")}</pre>
                    </details>
                    <label className="recovery-acknowledgement">
                      <input
                        name="saved-recovery-codes"
                        type="checkbox"
                        required
                      />
                      Guardei meus códigos em um local seguro
                    </label>
                  </div>
                )}
                <label>
                  {step === "verify" && useRecoveryCode
                    ? "Código de recuperação"
                    : "Código do autenticador"}
                  <input
                    key={useRecoveryCode ? "recovery-code" : "totp-code"}
                    name="code"
                    inputMode={
                      step === "verify" && useRecoveryCode ? "text" : "numeric"
                    }
                    pattern={
                      step === "verify" && useRecoveryCode
                        ? undefined
                        : "[0-9]{6}"
                    }
                    maxLength={step === "verify" && useRecoveryCode ? 64 : 6}
                    required
                    autoComplete="one-time-code"
                  />
                </label>
                {step === "verify" && (
                  <>
                    <button
                      className="button secondary"
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setUseRecoveryCode(!useRecoveryCode);
                        setMessage("");
                      }}
                    >
                      {useRecoveryCode
                        ? "Usar meu autenticador"
                        : "Usar um código de recuperação"}
                    </button>
                    {useRecoveryCode && (
                      <p className="muted">
                        Use um dos códigos guardados ao configurar sua conta.
                        Cada código funciona uma única vez.
                      </p>
                    )}
                  </>
                )}
              </>
            )}
            {message && (
              <p className="form-error" role="alert">
                {message}
              </p>
            )}
            <button className="button primary" disabled={busy}>
              {busy
                ? "Conferindo…"
                : step === "credentials"
                  ? "Entrar"
                  : step === "enroll" && !enrollment
                    ? "Configurar autenticador"
                    : "Confirmar código"}
            </button>
          </form>
        )}
      </Modal>
    </div>
  );
}
function Dashboard({
  data,
  prefix,
  month,
  base,
}: {
  data: Overview;
  prefix: string;
  month: string;
  base: string;
}) {
  const [composition, setComposition] = useState(false);
  const transactions = useQuery({
    queryKey: ["transactions", prefix, month, ""],
    queryFn: () =>
      api<{ data: Movement[] }>(
        `${prefix}/transactions?month=${month}&limit=6`,
      ),
  });
  const negative = data.surplusMinor !== null && minor(data.surplusMinor) < 0n;
  return (
    <>
      <div className="metrics">
        <button
          className={`main-metric ${negative ? "deficit" : ""}`}
          onClick={() => setComposition(true)}
        >
          <div className="metric-caption">
            Sobra prevista do orçamento <ArrowUpRight size={19} />
          </div>
          <Money value={data.surplusMinor} className="metric-value" />
          <span className="metric-description">
            Depois dos compromissos e do aporte planejado
          </span>
          <div className="metric-bottom">
            <span className="metric-tag">
              {data.quality === "reviewed"
                ? "Premissas revisadas"
                : "Projeção parcial"}
            </span>
            <span>
              Ver composição <ArrowRight size={15} />
            </span>
          </div>
        </button>
        <Link to={`${base}/contas`} className="metric panel">
          <div className="metric-caption">
            Saldo em contas{" "}
            <span className="metric-icon">
              <Landmark size={18} />
            </span>
          </div>
          <Money value={data.balanceMinor} className="metric-value" />
          <span className="metric-description">
            {data.accountsCount} contas incluídas · BRL
          </span>
          <div className="metric-note">
            <span className="status-dot" />
            Saldo informado pelas fontes
          </div>
        </Link>
        <Link to={`${base}/planejamento`} className="metric panel">
          <div className="metric-caption">
            Ainda a pagar{" "}
            <span className="metric-icon sand">
              <CalendarDays size={18} />
            </span>
          </div>
          <Money value={data.stillToPayMinor} className="metric-value" />
          <span className="metric-description">
            Compromissos conhecidos deste mês
          </span>
          <div className="metric-note">
            {data.upcoming.length} pagamentos previstos <ArrowRight size={15} />
          </div>
        </Link>
      </div>
      <div className="dashboard-columns">
        <div className="dashboard-left">
          <Panel
            title="Para onde foi seu dinheiro"
            subtitle="Consumo observado · sem pagamentos de fatura"
            action={<span className="small-tag">Este mês</span>}
          >
            <CategoryBars categories={data.categories} />
          </Panel>
          <Panel
            title="Últimos movimentos"
            subtitle="Um olhar sobre os lançamentos mais recentes"
            action={
              <Link className="text-link" to={`${base}/movimentos`}>
                Ver todos <ArrowRight size={15} />
              </Link>
            }
          >
            {transactions.isPending ? (
              <p role="status">Carregando movimentos…</p>
            ) : transactions.error ? (
              <p role="alert">Não foi possível carregar os movimentos.</p>
            ) : (
              <MovementList
                movements={transactions.data?.data.slice(0, 4) ?? []}
              />
            )}
          </Panel>
        </div>
        <div className="dashboard-right">
          <Panel
            title="Próximos pagamentos"
            subtitle="Para se organizar com antecedência"
            action={
              <span className="metric-icon sand">
                <CalendarDays size={18} />
              </span>
            }
          >
            {data.upcoming.length ? (
              <div className="upcoming-list">
                {data.upcoming.slice(0, 3).map((p) => (
                  <div className="upcoming-row" key={p.id}>
                    <span className="date-tile">
                      <DateLabel date={p.dueOn} />
                    </span>
                    <div>
                      <strong>{p.label}</strong>
                      <small>
                        {p.source === "planned"
                          ? "Previsto"
                          : "Valor informado"}
                      </small>
                    </div>
                    <Money value={p.amountMinor} />
                  </div>
                ))}
              </div>
            ) : (
              <Empty
                title="Nenhum compromisso informado"
                description="Adicione os próximos pagamentos para acompanhar seu mês."
              />
            )}
            <Link
              className="button secondary full-width"
              to={`${base}/planejamento`}
            >
              Abrir planejamento <ArrowRight size={16} />
            </Link>
          </Panel>
          <div className="assistant-teaser">
            <span className="assistant-orb">
              <Sparkles size={22} />
            </span>
            <h2>Um pouco mais de clareza.</h2>
            <p>
              Em breve, entenda seu mês com explicações ligadas aos seus
              números.
            </p>
            <Link to={`${base}/assistente`} className="text-link">
              Conhecer o assistente <ArrowRight size={16} />
            </Link>
            <span className="small-tag">Em preparação</span>
          </div>
          {data.issues.length > 0 && (
            <Panel
              title="Vale conferir"
              subtitle="Seu resumo tem algumas lacunas"
            >
              <ul className="issues-list">
                {data.issues.map((issue) => (
                  <li key={issue}>
                    <Info size={16} />
                    {issueLabels[issue] ?? "Uma informação precisa de revisão."}
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </div>
      </div>
      <div className="source-footer">
        <Info size={16} />
        <span>
          A sobra é uma previsão de orçamento. Ela não representa dinheiro
          disponível para gastar.
        </span>
      </div>
      <Modal
        open={composition}
        onOpenChange={setComposition}
        title="Como a sobra foi calculada"
        description={`Competência ${month} · valores conhecidos e previstos.`}
      >
        <div className="composition">
          {[
            ["Renda do mês", data.incomeMinor],
            ["Despesas fora dos cartões", data.directExpenseMinor],
            ["Consumo e compromissos dos cartões", data.cardsMinor],
            ["Antes do aporte", data.surplusBeforeReserveMinor],
            ["Aporte planejado para reserva", data.reserveContributionMinor],
            ["Sobra após o aporte", data.surplusMinor],
          ].map(([label, value]) => (
            <div key={label}>
              <span>{label}</span>
              <Money value={value ?? null} />
            </div>
          ))}
        </div>
        <p className="muted">
          Previsões conciliadas deixam de ser somadas. Pagamentos de cartão
          afetam o caixa, sem repetir o consumo.
        </p>
      </Modal>
    </>
  );
}
function CategoryBars({ categories }: { categories: Overview["categories"] }) {
  const hidden = useContext(Privacy),
    totals = new Map(categories.map((c) => [c.category, minor(c.amountMinor)]));
  const rows = [...totals]
      .filter(([, v]) => v > 0n)
      .sort((a, b) => (a[1] > b[1] ? -1 : 1)),
    total = rows.reduce((sum, [, v]) => sum + v, 0n);
  if (!rows.length)
    return (
      <Empty
        title="Seu mês começa aqui"
        description="Os gastos aparecem conforme você registra os movimentos."
      />
    );
  return (
    <>
      <div className="category-chart" aria-hidden="true">
        {rows.map(([name, value], index) => (
          <div key={name} className="category-row">
            <div>
              <span className={`category-dot color-${index % 4}`} />
              <span>{name}</span>
              <Money value={value.toString()} />
            </div>
            {!hidden && (
              <div className="bar-track">
                <span
                  className={`bar-fill color-${index % 4}`}
                  style={{
                    width: `${Number((value * 10000n) / total) / 100}%`,
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <table className="sr-only">
        <caption>Consumo por categoria</caption>
        <thead>
          <tr>
            <th>Categoria</th>
            <th>Valor</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([name, value]) => (
            <tr key={name}>
              <th>{name}</th>
              <td>
                <Money value={value.toString()} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
function MovementList({
  movements,
  onSelect,
}: {
  movements: Movement[];
  onSelect?: (movement: Movement) => void;
}) {
  if (!movements.length)
    return (
      <Empty
        title="Nenhum movimento por aqui"
        description="Confira o período ou comece registrando um lançamento."
      />
    );
  return (
    <div className="movement-list">
      {movements.map((t) => {
        const positive = minor(t.amountMinor) > 0n;
        const content = (
          <>
            <span className={`movement-icon ${positive ? "positive" : ""}`}>
              {positive ? (
                <ArrowDownLeft size={18} />
              ) : (
                <ArrowUpRight size={18} />
              )}
            </span>
            <div className="movement-name">
              <strong>{t.description}</strong>
              <small>
                {t.category} <span>·</span> <DateLabel date={t.occurredOn} />
                {t.kind === "card_purchase"
                  ? " · Cartão"
                  : t.kind === "card_payment"
                    ? " · Pagamento de fatura"
                    : ""}
                {t.status === "reversed" ? " · Anulado" : ""}
              </small>
            </div>
            <Money
              value={t.amountMinor}
              className={positive ? "positive-text" : ""}
            />
          </>
        );
        return onSelect ? (
          <button
            className="movement-row"
            key={t.id}
            onClick={() => onSelect(t)}
          >
            {content}
          </button>
        ) : (
          <div className="movement-row" key={t.id}>
            {content}
          </div>
        );
      })}
    </div>
  );
}
function Movements({
  prefix,
  month,
  demo,
}: {
  prefix: string;
  month: string;
  demo: boolean;
}) {
  const [search, setSearch] = useState(""),
    [page, setPage] = useState(1),
    [selected, setSelected] = useState<Movement | null>(null),
    [csvOpen, setCsvOpen] = useState(false);
  const data = useQuery({
    queryKey: ["transactions", prefix, month, search, page],
    queryFn: () =>
      api<{ data: Movement[]; meta: { total: number } }>(
        `${prefix}/transactions?month=${month}&q=${encodeURIComponent(search)}&page=${page}`,
      ),
  });
  const accountsQuery = useQuery({
    queryKey: ["accounts", prefix],
    queryFn: () => api<{ data: FinancialAccount[] }>(`${prefix}/accounts`),
  });
  return (
    <>
      <Panel>
        <div className="list-toolbar">
          <label className="search-input">
            <Search size={18} />
            <input
              aria-label="Buscar movimentos"
              placeholder="Buscar descrição ou categoria"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              className="button secondary"
              onClick={() => setCsvOpen(!csvOpen)}
              style={{ minHeight: 38 }}
            >
              <FileSpreadsheet size={16} />{" "}
              {csvOpen ? "Fechar importador" : "Importar extrato CSV"}
            </button>
            <span className="small-tag">
              {data.data?.meta.total ?? "…"} lançamentos
            </span>
          </div>
        </div>
        {data.isPending ? (
          <p role="status">Carregando…</p>
        ) : data.error ? (
          <p role="alert">{data.error.message}</p>
        ) : (
          <MovementList
            movements={data.data?.data ?? []}
            onSelect={(t) => {
              setSelected(t);
            }}
          />
        )}
        <div className="pagination">
          <button
            className="icon-button"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            aria-label="Página anterior"
          >
            <ChevronLeft size={18} />
          </button>
          <span>Página {page}</span>
          <button
            className="icon-button"
            disabled={page * 50 >= (data.data?.meta.total ?? 0)}
            onClick={() => setPage(page + 1)}
            aria-label="Próxima página"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </Panel>
      <details
        open={csvOpen}
        onToggle={(e) => setCsvOpen(e.currentTarget.open)}
        style={{ marginTop: 24 }}
      >
        <summary>Importação de extratos CSV</summary>
        {csvOpen && (
          <Suspense fallback={<p role="status">Preparando importador CSV…</p>}>
            <CsvImportPanel
              accounts={
                accountsQuery.data?.data.filter((a) => a.currency === "BRL") ??
                []
              }
              prefix={prefix}
              demo={demo}
            />
          </Suspense>
        )}
      </details>
      <Modal
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
        title={selected?.description ?? "Movimento"}
        description="Confira o realizado, corrija registros manuais e acompanhe o histórico."
      >
        {selected && (
          <>
            <div className="detail-amount">
              <Money value={selected.amountMinor} />
            </div>
            <p className="muted">
              Data do movimento: <DateLabel date={selected.occurredOn} /> ·
              Competência: {selected.competenceMonth}
            </p>
            {demo ? (
              <p className="notice-text">
                Este lançamento pertence à demonstração. A edição fica
                disponível nos seus próprios registros.
              </p>
            ) : (
              <Suspense fallback={<p role="status">Preparando registro…</p>}>
                <MovementDetail
                  movement={selected}
                  onDone={() => setSelected(null)}
                />
              </Suspense>
            )}
          </>
        )}
      </Modal>
    </>
  );
}
function Accounts({
  prefix,
  demo,
  month,
}: {
  prefix: string;
  demo: boolean;
  month: string;
}) {
  const [transfersOpen, setTransfersOpen] = useState(false);
  const [add, setAdd] = useState(false),
    [selected, setSelected] = useState<FinancialAccount | null>(null);
  const data = useQuery({
    queryKey: ["accounts", prefix],
    queryFn: () => api<{ data: FinancialAccount[] }>(`${prefix}/accounts`),
  });
  return (
    <>
      <Panel
        title="O que entra no seu resumo"
        subtitle="Cartões e contas têm papéis diferentes."
        action={
          !demo && (
            <button className="button primary" onClick={() => setAdd(true)}>
              <Plus size={16} />
              Adicionar conta
            </button>
          )
        }
      >
        {data.isPending ? (
          <p role="status">Carregando contas…</p>
        ) : data.error ? (
          <p role="alert">{data.error.message}</p>
        ) : data.data?.data.length ? (
          <div className="accounts-list">
            {data.data.data.map((a) => (
              <div key={a.id} className="account-row">
                <span className="account-icon">
                  {a.kind === "credit_card" ? (
                    <CreditCard size={23} />
                  ) : (
                    <Landmark size={23} />
                  )}
                </span>
                <div>
                  <strong>{a.name}</strong>
                  <small>
                    {a.institution} ·{" "}
                    {a.kind === "credit_card"
                      ? "Crédito"
                      : a.kind === "savings"
                        ? "Reserva"
                        : "Conta corrente"}
                  </small>
                  {a.balanceAsOf && (
                    <small>
                      Saldo informado em <DateLabel date={a.balanceAsOf} />
                    </small>
                  )}
                </div>
                {a.kind === "credit_card" ? (
                  <span className="small-tag">Fora do saldo em contas</span>
                ) : (
                  <Money value={a.balanceMinor} />
                )}
                {!demo && (
                  <button
                    className="button secondary"
                    aria-label={`Editar conta ${a.name}`}
                    onClick={() => setSelected(a)}
                  >
                    Editar
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <Empty
            title="Comece por uma conta"
            description="Informe um saldo conhecido ou deixe-o ausente até poder conferir."
          />
        )}
      </Panel>
      <Suspense fallback={<p role="status">Carregando conexões bancárias…</p>}>
        <OpenFinancePanel prefix={prefix} demo={demo} />
      </Suspense>
      <AccountForm open={add} onOpenChange={setAdd} />
      <details onToggle={(e) => setTransfersOpen(e.currentTarget.open)}>
        <summary>Transferências entre minhas contas</summary>
        {transfersOpen && (
          <Suspense fallback={<p role="status">Preparando transferências…</p>}>
            <TransfersPanel prefix={prefix} demo={demo} month={month} />
          </Suspense>
        )}
      </details>
      <Modal
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
        title="Editar conta"
        description="Atualize identificação ou saldo observado. O histórico ficará preservado."
      >
        {selected && (
          <Suspense fallback={<p role="status">Preparando conta…</p>}>
            <AccountEditor
              account={selected}
              onDone={() => setSelected(null)}
            />
          </Suspense>
        )}
      </Modal>
    </>
  );
}
function Assistant({ prefix }: { prefix: string }) {
  const data = useQuery({
    queryKey: ["ai", prefix],
    queryFn: () =>
      api<{ data: { configured: boolean; reason: string; model: string } }>(
        `${prefix}/ai/status`,
      ),
  });
  return (
    <Panel className="assistant-page">
      <div className="assistant-orb">
        <Sparkles size={28} />
      </div>
      <h2>Explicações que você pode conferir.</h2>
      <p>
        O assistente vai explicar os números calculados pelo Clareza e preparar
        sugestões para sua revisão.
      </p>
      <div className="assistant-prompts">
        <span>Explique minha sobra</span>
        <span>Qual o impacto de uma mudança?</span>
        <span>Ajude a revisar categorias</span>
      </div>
      <div className="notice-text">
        <ShieldCheck size={20} />
        <div>
          <strong>Assistente em preparação</strong>
          <p>
            {data.data?.data.reason ??
              "A IA não está ativada nesta versão local."}
          </p>
          <small>
            {data.data?.data.configured
              ? "Credencial detectada no servidor."
              : "Credencial ainda não detectada."}{" "}
            Nenhuma chamada foi enviada.
          </small>
        </div>
      </div>
    </Panel>
  );
}
function More({ base, demo }: { base: string; demo: boolean }) {
  return (
    <div className="more-grid">
      {[
        {
          path: "contas",
          icon: Landmark,
          title: "Contas e fontes",
          text: "Veja quais saldos entram no seu resumo.",
        },
        {
          path: "assistente",
          icon: Sparkles,
          title: "Assistente",
          text: "Disponibilidade e preparação da IA.",
        },
        {
          path: "planejamento",
          icon: CalendarDays,
          title: "Seu planejamento",
          text: "Agenda, orçamento e hipóteses.",
        },
      ].map(({ path, icon: Icon, title, text }) => (
        <Link className="panel more-card" key={path} to={`${base}/${path}`}>
          <Icon size={25} />
          <h2>{title}</h2>
          <p>{text}</p>
          <ArrowRight size={18} />
        </Link>
      ))}
      <Panel title="Privacidade e segurança">
        {!demo && (
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
            <ChangePassword />
            <RecoveryCodes />
          </div>
        )}

        <p>
          Esta versão mantém os dados no banco local. Exportação e backup
          protegido ainda estão em desenvolvimento.
        </p>
        <span className="small-tag">
          {demo ? "Demonstração fictícia" : "Acesso pessoal com segundo fator"}
        </span>
      </Panel>
    </div>
  );
}
type FormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  month?: string;
};
function AccountForm({ open, onOpenChange }: FormProps) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Adicionar conta"
      description="Informe apenas o que você já conhece."
    >
      <ManagedForm
        onDone={() => onOpenChange(false)}
        onSave={(f, save) =>
          save("/api/accounts", {
            name: f.get("name"),
            institution: f.get("institution"),
            kind: f.get("kind"),
            currency: "BRL",
            balanceMinor: String(f.get("balance")).trim()
              ? parseBRL(String(f.get("balance"))).toString()
              : null,
            balanceAsOf: f.get("date") || null,
          })
        }
      >
        <label>
          Nome da conta
          <input
            name="name"
            required
            maxLength={80}
            placeholder="Minha conta do dia a dia"
          />
        </label>
        <label>
          Instituição
          <input name="institution" required maxLength={80} />
        </label>
        <label>
          Tipo
          <select name="kind">
            <option value="checking">Conta corrente</option>
            <option value="savings">Reserva / poupança</option>
            <option value="credit_card">Cartão de crédito</option>
          </select>
        </label>
        <label>
          Saldo conhecido (opcional)
          <input
            name="balance"
            inputMode="decimal"
            placeholder="Não informado"
          />
        </label>
        <label>
          Data do saldo (se informado)
          <input name="date" type="date" />
        </label>
      </ManagedForm>
    </Modal>
  );
}
function EntryForm({ open, onOpenChange, month }: FormProps) {
  const data = useQuery({
    queryKey: ["accounts", "/api"],
    queryFn: () => api<{ data: FinancialAccount[] }>("/api/accounts"),
    enabled: open,
  });
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Registrar movimento"
      description="Para começar, registre receitas e despesas de contas. Compras de cartão exigem um ciclo de fatura."
    >
      {data.isPending ? (
        <p>Carregando suas contas…</p>
      ) : !data.data?.data.filter((a) => a.kind !== "credit_card").length ? (
        <p>Adicione uma conta na área Contas antes de registrar.</p>
      ) : (
        <ManagedForm
          onDone={() => onOpenChange(false)}
          onSave={(f, save) => {
            const kind = String(f.get("kind")),
              amount = parseBRL(String(f.get("amount")));
            if (amount <= 0n)
              throw new Error("Informe um valor maior que zero.");
            return save("/api/transactions/manual", {
              accountId: f.get("account"),
              description: f.get("description"),
              amountMinor: (kind === "income" ? amount : -amount).toString(),
              currency: "BRL",
              kind,
              occurredOn: f.get("date"),
              competenceMonth: month,
              category: f.get("category"),
              billId: null,
            });
          }}
        >
          <label>
            Descrição
            <input name="description" required maxLength={180} />
          </label>
          <label>
            Conta
            <select name="account">
              {data.data.data
                .filter((a) => a.kind !== "credit_card")
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Tipo
            <select name="kind">
              <option value="expense">Despesa</option>
              <option value="income">Receita</option>
            </select>
          </label>
          <label>
            Valor
            <input
              name="amount"
              inputMode="decimal"
              placeholder="0,00"
              required
            />
          </label>
          <label>
            Data
            <input
              name="date"
              type="date"
              defaultValue={`${month}-05`}
              required
            />
          </label>
          <label>
            Categoria
            <select name="category">
              {categories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
        </ManagedForm>
      )}
    </Modal>
  );
}
